import React from "react";
import lscache from "lscache";
import cache from "config/cache";
// Interfaces
import {
  State,
  Dispatch,
  GameProviderProps,
  Abilities,
  RoundState,
  Player,
  VetoState,
} from "./interfaces/GameContextInterface";
// Utilities
import { GameReducer } from "./utils/GameReducer";
// WebSocket
import { ws } from "api/webSocket";

const GameStateContext = React.createContext<State | undefined>(undefined);
const GameDispatchContext = React.createContext<Dispatch | undefined>(
  undefined
);

const initialState: State = {
  code: lscache.get(cache.GameCode) || null,
  isStarted: lscache.get(cache.GameIsStarted),
  roundState: lscache.get(cache.GameRoundState) || RoundState.PickingChancellor,
  hostId: lscache.get(cache.GameHostId) || null,
  player: lscache.get(cache.GamePlayer) || null,
  players: lscache.get(cache.GamePlayers) || [],
  playersNumber: lscache.get(cache.GamePlayersNumber) || null,
  remainingPlayersNumber: lscache.get(cache.GameRemainingPlayersNumber) || null,
  fascistsIndexes: lscache.get(cache.GameFascistsIndexes) || [],
  hitlerIndex: lscache.get(cache.GameHitlerIndex),
  liberalRulesApproval: lscache.get(cache.GameLiberalRulesApproval) || 0,
  fascistRulesApproval: lscache.get(cache.GameFascistRulesApproval) || 0,
  electionDeclined: lscache.get(cache.GameElectionDeclined) || 0,
  drawRules: lscache.get(cache.GameDrawRules) || [],
  discardRules: lscache.get(cache.GameDiscardRules) || [],
  gameRules: lscache.get(cache.GameGameRules) || null,
  isPresidentIndex: lscache.get(cache.GameIsPresidentIndex),
  isChancellorIndex: lscache.get(cache.GameIsChancellorIndex),
  wasPresidentIndex: lscache.get(cache.GameWasPresidentIndex),
  wasChancellorIndex: lscache.get(cache.GameWasChancellorIndex),
  dashboardRules: lscache.get(cache.GameDashboardRules) || [],
  activeAbility: lscache.get(cache.GameActiveAbility) || Abilities.None,
  chosenPlayerIndex: lscache.get(cache.GameChosenPlayerIndex),
  returnPresidentIndex: lscache.get(cache.GameReturnPresidentIndex),
  requestRestart: lscache.get(cache.GameRequestRestart) || false,
  vetoState: lscache.get(cache.GameVetoState) || VetoState.None,
};

function GameProvider({ children }: GameProviderProps) {
  const [state, dispatch] = React.useReducer(GameReducer, initialState);
  return (
    <GameStateContext.Provider value={state}>
      <GameDispatchContext.Provider value={dispatch}>
        {children}
      </GameDispatchContext.Provider>
    </GameStateContext.Provider>
  );
}

function useGameState() {
  const context = React.useContext(GameStateContext);
  if (context === undefined) {
    throw new Error("useGameState must be used within a GameProvider");
  }
  return context;
}

function useGameDispatch() {
  const context = React.useContext(GameDispatchContext);
  if (context === undefined) {
    throw new Error("useGameDispatch must be used within a GameProvider");
  }
  return context;
}

function useGame(): [State, Dispatch] {
  return [useGameState(), useGameDispatch()];
}

export {
  GameProvider,
  useGameState,
  useGameDispatch,
  useGame,
  // custom actions
  authenticate,
  updateState,
  resetState,
  setPlayersNumber,
  toggleModal,
  createPlayersAndAssignRoles,
  prepareRound,
  startAnotherRound,
  pickChancellor,
  handleVoting,
  presidentRejectRule,
  chancellorPassRule,
  prepareRevealAbility,
  finishRevealAbility,
  handleSearchAbilityChoose,
  handleSearchAbilityDone,
  handleNextPresidentChoose,
  killPlayer,
  requestVeto,
  answerVeto,
  toggleRequestRestart,
};

// custom actions
function authenticate(dispatch: Dispatch, payload: Player) {
  dispatch({
    type: "AUTHENTICATE",
    payload,
  });
}

function updateState(dispatch: Dispatch, payload: State, playerId: string) {
  dispatch({
    type: "UPDATE_STATE",
    payload,
  });

  if (playerId) {
    const player = payload.players.find((player) => player.id === playerId);
    if (player) authenticate(dispatch, player);
  }
}

function resetState(dispatch: Dispatch) {
  localStorage.clear();
  dispatch({
    type: "RESET_STATE",
  });
}

function setPlayersNumber(dispatch: Dispatch, payload: number) {
  // dispatch({
  //   type: "SET_PLAYERS_NUMBER",
  //   payload,
  // });
}

function toggleModal(dispatch: Dispatch) {
  // dispatch({
  //   type: "TOGGLE_MODAL",
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function createPlayersAndAssignRoles(dispatch: Dispatch, payload: string[]) {
  // dispatch({
  //   type: "CREATE_PLAYERS",
  //   payload,
  // });
  // dispatch({
  //   type: "ASSIGN_ROLES",
  // });
}

function prepareRound(dispatch: Dispatch) {
  // dispatch({
  //   type: "CHECK_RULES",
  // });
  // dispatch({
  //   type: "INIT_GOVERNMENT",
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function startAnotherRound(dispatch: Dispatch) {
  // dispatch({
  //   type: "CHECK_RULES",
  // });
  // dispatch({
  //   type: "NEXT_GOVERNMENT",
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function pickChancellor(code: number, chancellorId: string) {
  ws.pickChancellor(code, chancellorId);
  // dispatch({
  //   type: "PICK_CHANCELLOR",
  //   payload,
  // });
  // dispatch({
  //   type: "TOGGLE_MODAL",
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function handleVoting(code: number, isAgree: boolean) {
  ws.vote(code, isAgree);
  // dispatch({
  //   type: "HANDLE_VOTING",
  //   payload,
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function presidentRejectRule(code: number, index: number) {
  ws.presidentRejectRule(code, index);
  // dispatch({
  //   type: "PRESIDENT_REJECT_RULE",
  //   payload,
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function chancellorPassRule(code: number, index: number) {
  ws.chancellorPassRule(code, index);
  // dispatch({
  //   type: "CHANCELLOR_PASS_RULE",
  //   payload,
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function prepareRevealAbility(dispatch: Dispatch) {
  // dispatch({
  //   type: "CHECK_RULES",
  // });
  // dispatch({
  //   type: "TOGGLE_MODAL",
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function finishRevealAbility(code: number) {
  ws.makeAbilityDone(code);
  // dispatch({
  //   type: "ABILITY_DONE",
  // });
  // dispatch({
  //   type: "TOGGLE_MODAL",
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function handleSearchAbilityChoose(code: number, chosenId: string) {
  ws.searchChoose(code, chosenId);
  // dispatch({
  //   type: "SET_CHOSEN",
  //   payload,
  // });
  // dispatch({
  //   type: "TOGGLE_MODAL",
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function handleSearchAbilityDone(code: number) {
  ws.searchDone(code);
  // dispatch({
  //   type: "ABILITY_DONE",
  // });
  // dispatch({
  //   type: "SET_CHOSEN",
  //   payload: null,
  // });
  // dispatch({
  //   type: "TOGGLE_MODAL",
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function handleNextPresidentChoose(code: number, chosenId: string) {
  ws.nextPresident(code, chosenId);
  // dispatch({
  //   type: "SET_CHOSEN",
  //   payload,
  // });
  // dispatch({
  //   type: "ABILITY_HALF_DONE",
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function killPlayer(code: number, playerId: string) {
  ws.killPlayer(code, playerId);
  // dispatch({
  //   type: "KILL_PLAYER",
  //   payload,
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function requestVeto(code: number) {
  ws.requestVeto(code);
  // dispatch({
  //   type: "VETO",
  // });
  // dispatch({
  //   type: "TOGGLE_MODAL",
  // });
  // dispatch({
  //   type: "BACK_UP",
  // });
}

function answerVeto(code: number, answer: VetoState) {
  ws.answertVeto(code, answer);
}

function toggleRequestRestart(dispatch: Dispatch) {
  dispatch({
    type: "TOGGLE_REQUEST_RESTART",
  });
}
